import { useMemo, useRef } from 'react';

import type { Connector } from '@/types/core/connector';
import { flattenPages } from '@/types/shared/page';
import type { UUID } from '@/utils/uuid';

import { useConnectorRunHistory } from './use-connector-run-history';
import {
  computeTerminalMetrics,
  useResolvedConnectorStatuses,
} from './use-connector-terminal-metrics';
import { useListConnectorFeatures } from './use-list-connector-features';

export type ConnectorMetrics = {
  collected: number;
  changed: number;
  deleted: number;
  runChanged: number;
  runDeleted: number;
  failedCount: number;
  hasData: boolean;
  lastSync: Date | undefined;
  isLoading: boolean;
};

/**
 * Aggregates all per-connector metrics needed by the dashboard hero and related
 * summary surfaces. Combines current terminal statuses, previous statuses, run
 * history, and feature data into a single unified metrics object.
 */
export function useConnectorMetrics(
  tenantId: UUID,
  connector: Connector
): ConnectorMetrics {
  const {
    statuses,
    previousStatuses,
    capturedTerminal,
    isPending: isStatusesPending,
  } = useResolvedConnectorStatuses(tenantId, connector.id);

  const featuresQuery = useListConnectorFeatures(tenantId, connector.id, {
    limit: 100,
  });
  const featuresData = featuresQuery.data;
  const isFeaturesPending = featuresQuery.isPending;

  const collectors = useMemo(
    () =>
      flattenPages(featuresData?.pages).filter((f) => f.schedulable !== false),
    [featuresData]
  );

  const collectorIds = useMemo(
    () => new Set(collectors.map((f) => f.featureId)),
    [collectors]
  );

  const collectorFeatureIds = useMemo(
    () => collectors.map((f) => f.featureId),
    [collectors]
  );

  const { runMetrics } = useConnectorRunHistory(
    tenantId,
    connector.id,
    collectorFeatureIds,
    statuses
  );

  const runChanged = useMemo(
    () => runMetrics.reduce((sum, m) => sum + m.changed, 0),
    [runMetrics]
  );
  const runDeleted = useMemo(
    () => runMetrics.reduce((sum, m) => sum + m.deleted, 0),
    [runMetrics]
  );

  const computed = useMemo(
    () =>
      computeTerminalMetrics(
        statuses,
        previousStatuses,
        capturedTerminal,
        collectorIds
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [statuses, previousStatuses, collectorIds, capturedTerminal]
  );

  // Keep the last known good metrics to avoid flashing empty during
  // the brief window where previousStatuses hasn't resolved yet.
  const lastKnown = useRef(computed);
  if (computed.hasData) {
    lastKnown.current = computed;
  }

  const isLoading = isStatusesPending || isFeaturesPending;
  const metrics = computed.hasData ? computed : lastKnown.current;
  return { ...metrics, runChanged, runDeleted, isLoading };
}
